import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type MenuMode = 'static' | 'overlay' | 'slim' | 'slim-plus';

export interface LayoutState {
  staticMenuDesktopInactive: boolean;
  overlayMenuActive: boolean;
  sidebarActive: boolean;
  anchored: boolean;
}

@Injectable({ providedIn: 'root' })
export class LayoutService {
  private platformId = inject(PLATFORM_ID);

  menuMode = signal<MenuMode>('static');

  state = signal<LayoutState>({
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    sidebarActive: false,
    anchored: false,
  });

  isMobile(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    return window.innerWidth <= 991;
  }

  isSlim(): boolean { return this.menuMode() === 'slim'; }
  isSlimPlus(): boolean { return this.menuMode() === 'slim-plus'; }
  isOverlay(): boolean { return this.menuMode() === 'overlay'; }
  isStatic(): boolean { return this.menuMode() === 'static'; }

  toggleMenu(): void {
    if (this.isMobile()) {
      this.state.update(s => ({ ...s, overlayMenuActive: !s.overlayMenuActive }));
    } else if (this.isStatic()) {
      this.state.update(s => ({ ...s, staticMenuDesktopInactive: !s.staticMenuDesktopInactive }));
    } else if (this.isOverlay()) {
      this.state.update(s => ({ ...s, overlayMenuActive: !s.overlayMenuActive }));
    }
  }

  onSidebarMouseEnter(): void {
    if ((this.isSlim() || this.isSlimPlus()) && !this.state().anchored) {
      this.state.update(s => ({ ...s, sidebarActive: true }));
    }
  }

  onSidebarMouseLeave(): void {
    if ((this.isSlim() || this.isSlimPlus()) && !this.state().anchored) {
      this.state.update(s => ({ ...s, sidebarActive: false }));
    }
  }

  toggleAnchor(): void {
    this.state.update(s => ({ ...s, anchored: !s.anchored, sidebarActive: !s.anchored }));
  }

  closeOverlay(): void {
    this.state.update(s => ({ ...s, overlayMenuActive: false }));
  }

  setMenuMode(mode: MenuMode): void {
    this.menuMode.set(mode);
    this.state.update(s => ({
      ...s,
      staticMenuDesktopInactive: false,
      overlayMenuActive: false,
      sidebarActive: false,
      anchored: false,
    }));
  }
}
